import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plug, Unplug, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import { visionApi } from "@/lib/visionApi";
import { appConfirm } from "@/components/AppDialogHost";

export default function VisionProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState(null); // provider key
  const [keyInput, setKeyInput] = useState("");
  const [labelInput, setLabelInput] = useState("");
  const [show, setShow] = useState(false);
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await visionApi.providers();
      setProviders(r.providers || []);
    } catch { toast.error("Could not load providers"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startConnect = (p) => {
    setActiveForm(p.key);
    setKeyInput("");
    setLabelInput(p.label || "");
    setShow(false);
  };

  const submitConnect = async (key) => {
    if (!keyInput.trim()) { toast.error("API key required"); return; }
    setWorking(true);
    try {
      await visionApi.connectProvider(key, { api_key: keyInput.trim(), label: labelInput.trim() });
      toast.success(`Connected ${key}`);
      setActiveForm(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to connect"); }
    finally { setWorking(false); }
  };

  const disconnect = async (key) => {
    if (!await appConfirm(`Disconnect ${key}?`)) return;
    try {
      await visionApi.disconnectProvider(key);
      toast.success("Disconnected");
      load();
    } catch { toast.error("Failed to disconnect"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto animate-fade-up" data-testid="vision-providers-page">
      <Link to="/dashboard/vision-studio" data-testid="back-to-vision" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber transition mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Vision Studio
      </Link>

      <PageHeader
        eyebrow="// AI VIDEO PROVIDERS"
        title="Connect your video generation accounts."
        description="Bring your own API keys for the AI video providers you want to use. Keys are encrypted at rest and never leave the server."
      />

      {loading ? (
        <div className="text-center text-xs font-mono text-ink-muted py-10">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {providers.map((p) => (
            <div key={p.key} data-testid={`provider-${p.key}`}
              className={`rounded-xl border p-4 ${p.connected ? "border-emerald-500/40 bg-emerald-500/5" : "border-line bg-bg-surface"}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-display font-bold text-lg">{p.label}</div>
                  <div className="text-[11px] text-ink-muted">{p.vendor} · {p.notes}</div>
                </div>
                {p.connected ? (
                  <span className="text-xs text-emerald-300 inline-flex items-center gap-1">● Connected</span>
                ) : (
                  <span className="text-xs text-ink-muted">○ Not connected</span>
                )}
              </div>

              {activeForm === p.key ? (
                <div className="mt-3 space-y-2">
                  <div className="relative">
                    <input
                      type={show ? "text" : "password"}
                      value={keyInput}
                      onChange={(e) => setKeyInput(e.target.value)}
                      data-testid={`key-input-${p.key}`}
                      placeholder={`Paste your ${p.label} API key`}
                      className="w-full rounded-md border border-line bg-bg-elevated text-ink-primary placeholder:text-ink-muted px-3 py-2 pr-9 text-xs font-mono focus:border-brand-amber focus:outline-none"
                    />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted">
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <input
                    value={labelInput}
                    onChange={(e) => setLabelInput(e.target.value)}
                    data-testid={`label-input-${p.key}`}
                    placeholder="Label (optional, e.g. Personal account)"
                    className="w-full rounded-md border border-line bg-bg-elevated text-ink-primary placeholder:text-ink-muted px-3 py-2 text-xs focus:border-brand-amber focus:outline-none"
                  />
                  <div className="flex gap-2">
                    <button onClick={() => submitConnect(p.key)} disabled={working} data-testid={`save-${p.key}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-brand-amber text-bg font-bold text-xs disabled:opacity-50">
                      {working ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />}
                      Save
                    </button>
                    <button onClick={() => setActiveForm(null)}
                      className="px-3 py-1.5 rounded-md border border-line text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mt-2">
                  {p.connected ? (
                    <>
                      <button onClick={() => startConnect(p)} data-testid={`update-${p.key}`} className="text-xs px-3 py-1.5 rounded-md border border-line">
                        Update key
                      </button>
                      <button onClick={() => disconnect(p.key)} data-testid={`disconnect-${p.key}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-line hover:text-brand-onair">
                        <Unplug className="h-3.5 w-3.5" /> Disconnect
                      </button>
                    </>
                  ) : (
                    <button onClick={() => startConnect(p)} data-testid={`connect-${p.key}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-brand-amber/10 text-brand-amber border border-brand-amber/30">
                      <Plug className="h-3.5 w-3.5" /> Connect
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
